import { FormControl, Grid, IconButton, InputLabel, MenuItem, OutlinedInput, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import axios from '../../util/Axios';
import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import Edit from '@mui/icons-material/Edit';
import { EditDoctor } from './EditDoctor';
import { BooleanDisplay } from '../../components/BooleanDisplay';

const textFieldStyle = { display: 'flex', width: '90%', margin: 'auto', marginTop: '32px', marginBottom: '32px', textAlign: 'center' };

export default function Doctors() {
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [filteredHospital, setFilteredHospital] = useState('');
  const [filteredEmail, setFilteredEmail] = useState('');
  const [filteredName, setFilteredName] = useState('');

  const [editDoctorOpen, setEditDoctorOpen] = useState(false);
  const [editDoctorId, setEditDoctorId] = useState(null);
  const [doctorName, setDoctorName] = useState(null);
  const [doctorPhone, setDoctorPhone] = useState(null);
  const [doctorHospital, setDoctorHospital] = useState(null);
  const [doctorCountry, setDoctorCountry] = useState(null);
  const [doctorEnabled, setDoctorEnabled] = useState(null);
  const [doctorAdmin, setDoctorAdmin] = useState(false);

  useEffect(() => {
    axios('/users/doctors').then((data) => {
      setDoctors(data.data);
      setHospitals(
        data.data
          .map((doctor) => doctor.hospital)
          .filter((v, i, a) => a.indexOf(v) === i)
          .sort()
      ); //filter unique
    });
  }, []);

  const handleEditOpen = (doctor) => {
    setEditDoctorId(doctor.uuid);
    setDoctorName(doctor.name);
    setDoctorPhone(doctor.phone);
    setDoctorHospital(doctor.hospital);
    setEditDoctorOpen(true);
    setDoctorCountry(doctor.country);
    setDoctorEnabled(doctor.enabled);
    setDoctorAdmin(doctor.adminRole === 'ADMIN');
  };

  const handleEditClose = () => {
    setEditDoctorId(null);
    setDoctorName(null);
    setDoctorPhone(null);
    setDoctorHospital(null);
    setEditDoctorOpen(false);
    setDoctorCountry(null);
    setDoctorEnabled(null);
  };

  const changeDoctor = () => {
    axios('/users/doctors/' + editDoctorId + '/edit', {
      method: 'PUT',
      data: {
        name: doctorName,
        phone: doctorPhone,
        hospital: doctorHospital,
        country: doctorCountry,
        enabled: doctorEnabled,
        adminRole: doctorAdmin ? 'ADMIN' : 'NONE'
      },
    }).then((data) => {
      handleEditClose();
      filterDoctors(filteredHospital, filteredName, filteredEmail);
    });
  };

  const filterDoctorsHandler = (hospital, name, email) => {
    axios('/users/doctors?hospital=' + hospital).then((data) => {
      let doctors = data.data;
      if (name) {
        doctors = doctors.filter((doc) => doc.name.toLowerCase().includes(name.toLowerCase()));
      }
      if (email) {
        doctors = doctors.filter((doc) => doc.email.toLowerCase().includes(email.toLowerCase()));
      }
      setDoctors(doctors);
    });
  };
  // eslint-disable-next-line
  const filterDoctors = useCallback(debounce(filterDoctorsHandler, 300), []);

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Typography
            sx={{
              width: '100%',
              textAlign: 'center',
              marginTop: '16px',
              fontSize: '2em',
            }}
          >
            Doctors
          </Typography>
        </Grid>

        <Grid item xs={4}>
          <FormControl sx={textFieldStyle}>
            <InputLabel id="hospital_label">Hospital</InputLabel>
            <Select
              labelId="hospital_label"
              input={<OutlinedInput label="Hospital" />}
              value={filteredHospital}
              onChange={(event) => {
                setFilteredHospital(event.target.value);
                filterDoctors(event.target.value, filteredName, filteredEmail);
              }}
            >
              <MenuItem value={''}>All</MenuItem>
              {hospitals.map((hospital) => (
                <MenuItem key={hospital} value={hospital}>
                  {hospital}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <TextField
            sx={textFieldStyle}
            label="Name"
            value={filteredName}
            onChange={(event) => {
              setFilteredName(event.target.value);
              filterDoctors(filteredHospital, event.target.value, filteredEmail);
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            sx={textFieldStyle}
            label="Email"
            value={filteredEmail}
            onChange={(event) => {
              setFilteredEmail(event.target.value);
              filterDoctors(filteredHospital, filteredName, event.target.value);
            }}
          />
        </Grid>
      </Grid>
      <TableContainer sx={{ paddingLeft: '20px', paddingRight: '20px' }}>
        <Table
          sx={{
            minWidth: 650,
          }}
          aria-label="simple table"
          size="small"
        >
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Hospital</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {doctors.map((doctor) => (
              <TableRow key={doctor.uuid} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>{doctor.name}</TableCell>
                <TableCell>{doctor.email}</TableCell>
                <TableCell>{doctor.phone}</TableCell>
                <TableCell>{doctor.hospital}</TableCell>
                <TableCell>{doctor.country}</TableCell>
                <TableCell>
                  <BooleanDisplay value={doctor.enabled} />
                </TableCell>
                <TableCell>
                  <IconButton variant="contained" onClick={() => handleEditOpen(doctor)}>
                    <Edit />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <EditDoctor
        editDoctorOpen={editDoctorOpen}
        handleEditClose={handleEditClose}
        doctorName={doctorName}
        setDoctorName={setDoctorName}
        doctorPhone={doctorPhone}
        setDoctorPhone={setDoctorPhone}
        doctorHospital={doctorHospital}
        setDoctorHospital={setDoctorHospital}
        changeDoctor={changeDoctor}
        doctorCountry={doctorCountry}
        setDoctorCountry={setDoctorCountry}
        doctorEnabled={doctorEnabled}
        setDoctorEnabled={setDoctorEnabled}
        doctorAdmin={doctorAdmin}
        setDoctorAdmin={setDoctorAdmin}
      />
    </>
  );
}
